import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { AppStateContext } from '../../contexts/AppStateContext';
import { TradingContext } from '../../contexts/TradingContext';
import WatchlistsPanel from '../../components/TradePanel/components/WatchlistsPanel';
import MiniStockChart from '../../components/shared/MiniStockChart';
import DashboardMobileSubPageLayout from '../../components/MobileNavigation/DashboardMobileSubPageLayout';
import DashboardPageHeader from '../../components/shared/DashboardPageHeader';
import useIsMobile from '../../hooks/useIsMobile';
import { getDashboardPageShellStyle, DASHBOARD_CONTENT_MAX_WIDTH } from '../../constants/dashboardLayout';
import { navigateToTradeWithSymbol } from '../../utils/tradeNavigation';
import { useNavigate } from 'react-router-dom';

// Watchlist column width when the delayed/live chart sits beside it.
const WATCHLIST_WITH_CHART_WIDTH = 340;
const DESKTOP_CONTENT_MIN_HEIGHT = 400;

/** Watchlists page embedded inside DashboardShell. */
export default function DashboardWatchlistsPage() {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { watchlistTickers } = useContext(AppStateContext);
  const { selectedAccount } = useContext(TradingContext);
  const showChart = !isMobile;
  const [selectedIndex, setSelectedIndex] = useState(0);

  const chartSymbol = useMemo(() => {
    if (!watchlistTickers || watchlistTickers.length === 0) return null;
    const clamped = Math.min(selectedIndex, watchlistTickers.length - 1);
    return watchlistTickers[clamped]?.ticker || null;
  }, [selectedIndex, watchlistTickers]);

  const handleTickerClick = useCallback((ticker) => {
    if (isMobile) {
      if (!navigateToTradeWithSymbol(navigate, ticker, user, selectedAccount)) {
        const idx = (watchlistTickers || []).findIndex((t) => t.ticker === ticker);
        setSelectedIndex(idx >= 0 ? idx : 0);
      }
      return;
    }
    const idx = (watchlistTickers || []).findIndex((t) => t.ticker === ticker);
    setSelectedIndex(idx >= 0 ? idx : 0);
  }, [isMobile, navigate, selectedAccount, user, watchlistTickers]);

  const handleTickerSelect = useCallback(() => {
    // Mobile WatchlistsPanel calls this after updating chart settings; when we're on
    // /dashboard/watchlists there is no trade chart — navigation happens in handleTickerClick.
  }, []);

  // Space bar scans through the watchlist so you can flip through charts without
  // reaching for the mouse. Skip when typing in an input/textarea/select or when a
  // modifier is held (so it doesn't fight with e.g. browser shortcuts).
  useEffect(() => {
    if (!showChart) return;
    const handleKeyDown = (e) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target;
      const tag = target?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        tag === 'BUTTON' ||
        tag === 'A' ||
        target?.isContentEditable
      ) return;

      const count = watchlistTickers?.length || 0;
      if (count === 0) return;
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % count);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showChart, watchlistTickers]);

  const watchlistCard = (constrainHeight = false) => (
    <div
      data-testid="watchlists-list-card"
      style={{
        background: isMobile ? colors.bgPrimary : colors.bgSecondary,
        border: isMobile ? 'none' : `1px solid ${colors.border}`,
        borderRadius: isMobile ? 0 : 10,
        overflow: 'hidden',
        minHeight: constrainHeight ? 0 : (isMobile ? undefined : DESKTOP_CONTENT_MIN_HEIGHT),
        height: constrainHeight ? '100%' : undefined,
        maxWidth: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        data-testid="watchlists-list-scroll"
        style={{
          overflowX: 'auto',
          overflowY: constrainHeight ? 'hidden' : 'visible',
          flex: constrainHeight ? 1 : undefined,
          minHeight: constrainHeight ? 0 : undefined,
          display: constrainHeight ? 'flex' : undefined,
          flexDirection: constrainHeight ? 'column' : undefined,
          maxWidth: '100%',
          width: '100%',
        }}
      >
        <WatchlistsPanel
          onTickerClick={handleTickerClick}
          onTickerSelect={isMobile ? handleTickerSelect : undefined}
          highlightedTicker={showChart ? chartSymbol : null}
        />
      </div>
    </div>
  );

  const content = (
    <div
      data-testid="watchlists-page-shell"
      style={{
        ...getDashboardPageShellStyle({ isMobile }),
        ...(isMobile ? { padding: undefined } : null),
        ...(showChart
          ? {
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              padding: '36px 72px 24px',
            }
          : null),
        maxWidth: isMobile
          ? '100%'
          : DASHBOARD_CONTENT_MAX_WIDTH,
      }}
    >
      {!isMobile && (
        <DashboardPageHeader
          title="Watchlists"
          description="Manage your tracked symbols and monitor price movements"
        />
      )}
      {showChart ? (
        <>
          <div
            data-testid="watchlists-desktop-row"
            style={{
              display: 'flex',
              gap: 20,
              alignItems: 'stretch',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <div
              data-testid="watchlists-list-column"
              style={{
                flex: `0 0 ${WATCHLIST_WITH_CHART_WIDTH}px`,
                maxWidth: WATCHLIST_WITH_CHART_WIDTH,
                display: 'flex',
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              {watchlistCard(true)}
            </div>
            <div
              data-testid="watchlists-chart-column"
              style={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                background: colors.bgSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                overflow: 'hidden',
              }}
            >
              <MiniStockChart symbol={chartSymbol} colors={colors} fillHeight />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, marginTop: 12, flexShrink: 0 }}>
            <div style={{ flex: `0 0 ${WATCHLIST_WITH_CHART_WIDTH}px` }} />
            <div
              style={{
                flex: 1,
                minWidth: 0,
                color: colors.textMuted,
                fontSize: '0.875rem',
                fontStyle: 'italic',
                textAlign: 'center',
              }}
            >
              Press space to go to next symbol in the list.
            </div>
          </div>
        </>
      ) : (
        watchlistCard(false)
      )}
    </div>
  );

  if (isMobile) {
    return (
      <DashboardMobileSubPageLayout title="Watchlists" showBack={false} flushContent>
        {content}
      </DashboardMobileSubPageLayout>
    );
  }

  return content;
}
